import { Helmet } from "react-helmet";
import React from "react";
import { Box, Paper, Typography, Link } from "@mui/material";

const EFFECTIVE_DATE = "October 4, 2025";
const GOVERNING_LAW = "State of California, USA"; // change as needed

const TermsOfService: React.FC = () => (
  <Box
    component="main"
    id="main-content"
    tabIndex={-1}
    sx={{
      flex: 1,
      width: "100%",
      minHeight: 0,
      py: 6,
      display: "flex",
      flexDirection: "column",
    }}
  >
    <Paper
      sx={{
        p: { xs: 2, sm: 5 },
        maxWidth: 800,
        mx: "auto",
        borderRadius: 5,
        boxShadow: 5,
        animation: "slideUp 0.55s ease-out both",
        "@media (prefers-reduced-motion: reduce)": { animation: "none" },
      }}
    >
      <Helmet>
        <title>Terms of Service & Disclaimer | Rate My Cribs</title>
        <meta
          name="description"
          content="Lean Terms of Service for Rate My Cribs."
        />
        <link rel="canonical" href="https://ratemycribs.com/terms" />
      </Helmet>

      <Typography
        variant="h4"
        fontWeight={800}
        color="primary"
        align="center"
        gutterBottom
      >
        Terms of Service & Disclaimer
      </Typography>

      <Typography variant="body2" sx={{ mb: 3, color: "text.secondary" }}>
        Effective Date: {EFFECTIVE_DATE}
      </Typography>

      <Typography variant="body1" sx={{ mb: 2 }}>
        By accessing or using <b>Rate My Cribs</b> (“we,” “us,” “our”), you
        agree to these Terms. If you don’t agree, do not use the site. We may
        update these Terms by posting a revised version on this page; continued
        use constitutes acceptance.
      </Typography>

      <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
        1) Eligibility
      </Typography>
      <Typography variant="body1" sx={{ mb: 2 }}>
        You must be at least 18 years old (or the age of majority in your
        jurisdiction) to use the site.
      </Typography>

      <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
        2) User Content & License
      </Typography>
      <Typography variant="body1" sx={{ mb: 2 }}>
        Reviews, ratings, text, and other materials you submit (“User Content”)
        are your sole responsibility. We do not verify or guarantee accuracy. By
        submitting User Content, you represent you have the rights to post it,
        and you grant us a worldwide, non-exclusive, royalty-free, transferable,
        sublicensable license to host, use, reproduce, modify, distribute, and
        display it to operate and promote the site.
      </Typography>

      <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
        3) Prohibited Content
      </Typography>
      <ul
        style={{
          fontSize: 16,
          color: "#333",
          marginBottom: 16,
          paddingLeft: 20,
        }}
      >
        <li>
          Defamatory/libelous statements presented as fact; unlawful threats or
          harassment;
        </li>
        <li>
          Personal data or doxxing (e.g., phone numbers, exact unit numbers)
          without consent;
        </li>
        <li>
          Copyright/trademark infringement or rights-of-publicity violations;
        </li>
        <li>
          Deceptive/misleading content, spam, or content that disrupts site
          operation.
        </li>
      </ul>

      <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
        4) Moderation & Takedown
      </Typography>
      <Typography variant="body1" sx={{ mb: 2 }}>
        We may remove, edit, refuse, or restore content at our sole discretion
        and may restrict access for violations. To report problematic content,
        email{" "}
        <Link href="mailto:info.ratemycribs@gmail.com">
          info.ratemycribs@gmail.com
        </Link>{" "}
        with the URL, description, and reason.
      </Typography>

      <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
        5) No Endorsement; Disclaimers
      </Typography>
      <Typography variant="body1" sx={{ mb: 2 }}>
        Reviews reflect user opinions only and do not represent our views. The
        site and its content are provided “AS IS” and “AS AVAILABLE.” To the
        fullest extent permitted by law, we disclaim all warranties, including
        accuracy, reliability, merchantability, and fitness for a particular
        purpose. We do not provide legal, financial, or housing advice.
      </Typography>

      <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
        6) Limitation of Liability
      </Typography>
      <Typography variant="body1" sx={{ mb: 2 }}>
        To the maximum extent permitted by law, we are not liable for indirect,
        incidental, special, consequential, or punitive damages, or loss of
        profits, data, or goodwill arising from or related to your use of the
        site or any User Content.
      </Typography>

      <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
        7) Indemnification
      </Typography>
      <Typography variant="body1" sx={{ mb: 2 }}>
        You agree to defend, indemnify, and hold us harmless from claims and
        expenses (including reasonable attorneys’ fees) arising out of your User
        Content or your violation of these Terms or any law or third-party
        rights.
      </Typography>

      <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
        8) Intellectual Property
      </Typography>
      <Typography variant="body1" sx={{ mb: 2 }}>
        Except for User Content, the site design, logos, and other materials are
        our property or licensed to us and protected by IP laws. Don’t copy or
        create derivative works without permission.
      </Typography>

      <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
        9) Governing Law & Venue
      </Typography>
      <Typography variant="body1" sx={{ mb: 2 }}>
        These Terms are governed by the laws of the {GOVERNING_LAW}. You agree
        to exclusive jurisdiction and venue in courts located there.
      </Typography>

      <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
        10) Privacy
      </Typography>
      <Typography variant="body1" sx={{ mb: 2 }}>
        See our{" "}
        <Link href="/privacy" underline="hover">
          Privacy Notice
        </Link>
        .
      </Typography>

      <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
        Contact
      </Typography>
      <Typography variant="body1">
        Questions? Email{" "}
        <Link href="mailto:info.ratemycribs@gmail.com">
          info.ratemycribs@gmail.com
        </Link>
        .
      </Typography>
    </Paper>
  </Box>
);

export default TermsOfService;
